import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useState } from 'react'
import { useQueryProducts } from '@@partner:graphql/query/catalog/products.gql'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { IconButton } from 'rsuite'
import LegacyDownloadIcon from '@rsuite/icons/legacy/Download'

import InfiniteScroll from 'react-infinite-scroller/dist/InfiniteScroll'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import ProductsTable, { showSettingsType } from './grid/ProductsTable'

import useFetchMorePagination from '@@partner:helpers/hooks/useFetchMorePagination'

import {
  ROUTE_CATALOG_EXPORT,
  ROUTE_CATALOG_PRODUCT_EDIT
} from '@@partner:config/routes'
import ProductsFilter from '@components/catalog/product/grid/ProductsFilter'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import ProductsTableShowSettings from './grid/ProductsTableShowSettings'
import ProductBulkUpdatePanel from './grid/ProductBulkUpdatePanel'

export interface ProductGridProps {
  brandSlug: string
}

const DEFAULT_SHOW_SETTINGS = {
  shopsIds: []
}

const PRODUCTS_PER_PAGE = 20

export default function ProductGrid ({ brandSlug }: ProductGridProps) {
  const history = useHistory()
  const { search } = useLocation()

  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })
  const [ showSettings, setShowSettings ] = useState<showSettingsType>(DEFAULT_SHOW_SETTINGS)
  const {
    tableSort,
    handleSortColumn
  } = useGridHelper()
  const [ selected, setSelected ] = useState<GQL_ID[]>([])
  const [ showBulkUpdate, setShowBulkUpdate ] = useState(false)

  const { data, loading, error, fetchMore } = useQueryProducts({
    pager: {
      page: 1,
      limit: PRODUCTS_PER_PAGE
    },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    },
    filter
  })

  const updaterFunction = useCallback((cached: any, fetched: any) => {
    const prev = cached?.products?.edges ?? []
    const curr = fetched?.products?.edges ?? []

    return {
      hasMore: curr.length > 0,
      result: {
        products: {
          ...fetched.products,
          edges: [ ...prev, ...curr ]
        }
      }
    }
  }, [])

  const wrappedFetchMore = useCallback(({ variables: vars, ...rest }: any) => {
    return fetchMore({
      ...rest,
      variables: {
        pager: { page: vars?.page?.page ?? 1, limit: PRODUCTS_PER_PAGE },
        filter,
        sort: { fieldName: tableSort.fieldName, direction: tableSort.direction }
      }
    })
  }, [ fetchMore, filter, tableSort ])

  const { hasMore, loadingMore, fetchNextPage } = useFetchMorePagination(
    {
      fetchMore: wrappedFetchMore,
      updaterFunction
    },
    [ filter, tableSort.fieldName, tableSort.direction ]
  )

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
    },
    []
  )

  const generateEditUrl = useCallback(
    (productId) => {
      if (!brandSlug) {
        return ''
      }
      return generatePath(ROUTE_CATALOG_PRODUCT_EDIT, {
        brandSlug,
        productId
      })
    },
    [ brandSlug ]
  )

  const pageInfo = data?.products.pageInfo

  const handleOpenExport = useCallback(() => {
    history.push({
      pathname: generatePath(ROUTE_CATALOG_EXPORT, { brandSlug }),
      search
    })
  }, [ brandSlug, history, search ])

  const renderToolbar = useCallback(() => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <ProductsTableShowSettings
        onSelect={setShowSettings}
      />
    </div>
  ), [])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки товаров"
        error={error}
      />
    )
  }

  const handleSelectProduct = (id: GQL_ID) => {
    const includes = selected.includes(id)
    if (includes) {
      setSelected((selected) => selected.filter((val) => val !== id))
    }
    else {
      setSelected((selected) => [ ...selected, id ])
    }
  }

  const products = data?.products.edges ?? []

  const hasFilters = filter.and && filter.and.some((filter) => filter.op !== 'unwrap') ? true : false

  return (
    <div>
      <ProductsFilter
        onFilterChanged={handleFilterChanged}
        showBulkUpdate={showBulkUpdate}
        onShowBulkUpdate={setShowBulkUpdate}
        canBulkUpdate={hasFilters}
        submitGroupExtra={
          <IconButton
            icon={<LegacyDownloadIcon />}
            appearance="ghost"
            onClick={handleOpenExport}
            type="button"
          >
            Экспорт XLSX
          </IconButton>
        }
      />

      <ProductBulkUpdatePanel
        show={selected.length > 0 || showBulkUpdate}
        filter={hasFilters && showBulkUpdate ? filter : undefined}
        selectedIds={selected}
      />

      <InfiniteScroll
        loadMore={fetchNextPage}
        hasMore={hasMore}
        initialLoad={false}
        loader={<AppLoader.Inline key="loader" center />}
      >
        <ProductsTable
          tableSort={tableSort}
          loading={loading && !loadingMore}
          products={products}
          generateEditUrl={generateEditUrl}
          onSortColumn={handleSortColumn}
          showSettings={showSettings}
          selected={selected}
          onSelect={handleSelectProduct}
          renderToolbar={renderToolbar}
          total={pageInfo?.total}
        />
      </InfiniteScroll>
    </div>
  )
}
